import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ScatterChart, Scatter
} from 'recharts';
import './AdvancedOptionsDashboard.css';

function AdvancedOptionsDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('ytd');
  const [infoModal, setInfoModal] = useState({ open: false, title: '', content: '' });
  
  // Goal: €20,000 + 20% = €24,000 by September next year
  const GOAL_TARGET = 24000;
  const GOAL_START = 20000;
  const GOAL_MONTHS = 12; // From now to September next year
  const GOAL_START_DATE = new Date();
  const GOAL_END_DATE = new Date();
  GOAL_END_DATE.setMonth(GOAL_END_DATE.getMonth() + GOAL_MONTHS);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [equityRes, positionsRes, tradesRes, accountsRes, pmccRes] = await Promise.all([
        axios.get(`${API_URL}/portfolio/equity/`),
        axios.get(`${API_URL}/portfolio/open-positions/?page_size=all`),
        axios.get(`${API_URL}/portfolio/trades/?page_size=all`),
        axios.get(`${API_URL}/portfolio/accounts/?page_size=all`),
        axios.get(`${API_URL}/portfolio/pmcc/?page_size=all`)
      ]);

      const processedData = processData(
        equityRes.data,
        positionsRes.data,
        tradesRes.data,
        accountsRes.data,
        pmccRes.data
      );
      setData(processedData);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const processData = (equityData, positionsData, tradesData, accountsData, pmccData) => {
    // Parse dates helper
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(2000 + parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      }
      return new Date(dateStr);
    };

    const equity = Array.isArray(equityData.results || equityData) ? (equityData.results || equityData) : [];
    const positions = Array.isArray(positionsData.results || positionsData) ? (positionsData.results || positionsData) : [];
    const trades = Array.isArray(tradesData.results || tradesData) ? (tradesData.results || tradesData) : [];
    const accounts = Array.isArray(accountsData.results || accountsData) ? (accountsData.results || accountsData) : [];
    const pmccs = Array.isArray(pmccData.results || pmccData) ? (pmccData.results || pmccData) : [];

    // Get latest equity
    const sortedEquity = equity.sort((a, b) => {
      const dateA = parseDate(a.report_date);
      const dateB = parseDate(b.report_date);
      return (dateB || 0) - (dateA || 0);
    });
    const currentEquity = parseFloat(sortedEquity[0]?.total || 0);

    // Calculate goal progress
    const goalProgress = ((currentEquity - GOAL_START) / (GOAL_TARGET - GOAL_START)) * 100;
    const monthlyTarget = (GOAL_TARGET - GOAL_START) / GOAL_MONTHS;
    const monthsElapsed = Math.floor((new Date() - GOAL_START_DATE) / (1000 * 60 * 60 * 24 * 30));
    const expectedEquity = GOAL_START + (monthlyTarget * monthsElapsed);

    // Monthly equity breakdown
    const monthlyEquity = {};
    sortedEquity.forEach(eq => {
      const date = parseDate(eq.report_date);
      if (date) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyEquity[monthKey]) {
          monthlyEquity[monthKey] = { equity: 0, target: 0, date: date };
        }
        monthlyEquity[monthKey].equity = Math.max(monthlyEquity[monthKey].equity, parseFloat(eq.total || 0));
      }
    });

    // Calculate monthly targets
    Object.keys(monthlyEquity).forEach((key, idx) => {
      monthlyEquity[key].target = GOAL_START + (monthlyTarget * (idx + 1));
      monthlyEquity[key].difference = monthlyEquity[key].equity - monthlyEquity[key].target;
    });

    // Weekly P&L
    const weeklyPnL = {};
    const closedTrades = trades.filter(t => {
      const indicator = (t.open_close_indicator || '').toUpperCase();
      return indicator === 'C' || indicator === 'CLOSE';
    });

    closedTrades.forEach(trade => {
      const date = parseDate(trade.trade_date);
      if (date) {
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const weekStart = new Date(date);
        weekStart.setDate(diff);
        weekStart.setHours(0, 0, 0, 0);
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyPnL[weekKey]) {
          weeklyPnL[weekKey] = { pnl: 0, trades: 0, date: weekStart };
        }
        weeklyPnL[weekKey].pnl += parseFloat(trade.fifo_pnl_realized || 0);
        weeklyPnL[weekKey].trades += 1;
      }
    });

    // Covered calls income per month
    const coveredCallIncome = {};
    const openingTrades = trades.filter(t => {
      const indicator = (t.open_close_indicator || '').toUpperCase();
      return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL' && t.put_call === 'C';
    });

    openingTrades.forEach(trade => {
      const date = parseDate(trade.trade_date);
      if (date) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!coveredCallIncome[monthKey]) {
          coveredCallIncome[monthKey] = { income: 0, count: 0, date: date };
        }
        // Income is negative trade_money for SELL (premium received)
        coveredCallIncome[monthKey].income += Math.abs(parseFloat(trade.trade_money || 0));
        coveredCallIncome[monthKey].count += 1;
      }
    });

    // Calculate performance metrics
    const winningTrades = closedTrades.filter(t => parseFloat(t.fifo_pnl_realized || 0) > 0);
    const losingTrades = closedTrades.filter(t => parseFloat(t.fifo_pnl_realized || 0) < 0);
    const winRate = closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0;
    
    const totalProfit = winningTrades.reduce((sum, t) => sum + parseFloat(t.fifo_pnl_realized || 0), 0);
    const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + parseFloat(t.fifo_pnl_realized || 0), 0));
    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? 999 : 0;

    // Greeks exposure
    const greeksExposure = positions.reduce((acc, pos) => {
      if (pos.asset_category === 'OPT') {
        const delta = parseFloat(pos.delta || 0);
        const gamma = parseFloat(pos.gamma || 0);
        const theta = parseFloat(pos.theta || 0);
        const vega = parseFloat(pos.vega || 0);
        const positionValue = parseFloat(pos.position || 0);
        
        acc.delta += delta * positionValue * 100; // Assuming 100 multiplier
        acc.gamma += gamma * positionValue * 100;
        acc.theta += theta * positionValue * 100;
        acc.vega += vega * positionValue * 100;
      }
      return acc;
    }, { delta: 0, gamma: 0, theta: 0, vega: 0 });

    // Max drawdown
    let peak = GOAL_START;
    let maxDrawdown = 0;
    sortedEquity.forEach(eq => {
      const equity = parseFloat(eq.total || 0);
      if (equity > peak) peak = equity;
      const drawdown = ((peak - equity) / peak) * 100;
      if (drawdown > maxDrawdown) maxDrawdown = drawdown;
    });

    // Position distribution
    const positionDistribution = {
      longCalls: positions.filter(p => p.asset_category === 'OPT' && p.put_call === 'C' && parseFloat(p.position || 0) > 0).length,
      shortCalls: positions.filter(p => p.asset_category === 'OPT' && p.put_call === 'C' && parseFloat(p.position || 0) < 0).length,
      longPuts: positions.filter(p => p.asset_category === 'OPT' && p.put_call === 'P' && parseFloat(p.position || 0) > 0).length,
      shortPuts: positions.filter(p => p.asset_category === 'OPT' && p.put_call === 'P' && parseFloat(p.position || 0) < 0).length,
      stocks: positions.filter(p => p.asset_category === 'STK').length,
    };

    return {
      currentEquity,
      goalProgress: Math.max(0, Math.min(100, goalProgress)),
      expectedEquity,
      monthlyEquity: Object.values(monthlyEquity).sort((a, b) => a.date - b.date),
      weeklyPnL: Object.values(weeklyPnL).sort((a, b) => a.date - b.date).slice(-12),
      coveredCallIncome: Object.values(coveredCallIncome).sort((a, b) => a.date - b.date),
      winRate,
      profitFactor,
      totalTrades: closedTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      totalProfit,
      totalLoss,
      greeksExposure,
      maxDrawdown,
      positionDistribution,
      totalPositions: positions.length,
      totalAccounts: accounts.length,
      totalPMCCs: pmccs.length,
      positions,
      trades: closedTrades,
    };
  };

  if (loading || !data) {
    return <div className="dashboard-loading">Loading advanced dashboard...</div>;
  }

  const formatCurrency = (value) => {
    return `€ ${parseFloat(value || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  // Prepare chart data
  const monthlyChartData = data.monthlyEquity.map(m => ({
    month: m.date.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' }),
    equity: m.equity,
    target: m.target,
    difference: m.difference
  }));

  const weeklyChartData = data.weeklyPnL.map(w => ({
    week: w.date.toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' }),
    pnl: w.pnl,
    trades: w.trades
  }));

  const coveredCallChartData = data.coveredCallIncome.map(cc => ({
    month: cc.date.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' }),
    income: cc.income,
    count: cc.count
  }));

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe'];

  // Functionaliteit uitleg data
  const functionalityInfo = {
    goal: {
      title: 'Jaardoel Tracking',
      content: 'Visualiseert de voortgang naar het jaardoel van €20.000 + 20% = €24.000. Toont huidige equity vs. doelbedrag, berekent percentage voortgang, en toont verwachte equity op basis van lineaire groei. Helpt je gefocust te blijven op je lange-termijn doelen.'
    },
    monthly: {
      title: 'Maandelijkse Equity Breakdown',
      content: 'Toont equity per maand vergeleken met maandelijkse targets. Berekent maandelijkse equity waarden, vergelijkt met targets, toont verschil (boven/onder target), en markeert maanden die gehaald zijn (✓) of gemist (✗). Geeft maandelijks inzicht in of je op schema bent.'
    },
    weekly: {
      title: 'Weekelijkse P&L Overzicht',
      content: 'Visualiseert wekelijkse winst/verlies performance. Groepeert trades per week, toont totale P&L per week en aantal trades. Helpt bij het identificeren van wekelijkse patronen en trends in je trading performance.'
    },
    coveredCalls: {
      title: 'Covered Calls Opbrengst per Maand',
      content: 'Toont maandelijkse inkomsten uit covered call strategieën. Berekent totale premium ontvangen per maand, toont aantal covered call trades, en berekent gemiddelde opbrengst per trade. Helpt bij het tracken van passieve inkomsten uit covered calls.'
    },
    winRate: {
      title: 'Win Rate',
      content: 'Berekent het percentage winstgevende trades. Telt winstgevende vs. verliesgevende trades en berekent percentage: (wins / totaal) × 100. Essentiële metric om te bepalen of je strategie consistent winstgevend is.'
    },
    profitFactor: {
      title: 'Profit Factor',
      content: 'Berekent de verhouding tussen totale winst en totale verlies. Formule: Totale Winst / Totale Verlies. Waarde > 1.0 betekent winstgevend. Hogere waarde = betere performance. Belangrijke metric die laat zien of je winsten groter zijn dan je verliezen.'
    },
    drawdown: {
      title: 'Max Drawdown',
      content: 'Toont de maximale daling van peak naar trough. Berekent grootste daling in portfolio waarde als percentage van peak waarde. Cruciaal voor risicomanagement - laat zien wat je maximale verlies kan zijn.'
    },
    delta: {
      title: 'Delta Exposure',
      content: 'Toont totale portfolio delta sensitivity. Berekent som van alle position deltas. Positief = bullish, Negatief = bearish. Helpt bij het begrijpen van je markt exposure en directional bias.'
    },
    gamma: {
      title: 'Gamma Exposure',
      content: 'Toont totale portfolio gamma sensitivity. Berekent som van alle position gammas. Toont hoe snel delta verandert bij beweging. Hoge gamma = snelle delta veranderingen. Belangrijk voor het begrijpen van hoe je exposure verandert bij marktbewegingen.'
    },
    theta: {
      title: 'Theta (Time Decay)',
      content: 'Toont dagelijkse time decay. Berekent totale portfolio theta. Toont verwachte time decay per dag. Cruciaal voor optie traders - laat zien hoeveel je dagelijks verliest/verdient aan time decay.'
    },
    vega: {
      title: 'Vega Exposure',
      content: 'Toont totale portfolio volatility sensitivity. Berekent som van alle position vegas. Toont impact van 1% IV verandering. Positief = profiteert van stijgende IV. Helpt bij het begrijpen van je exposure aan volatility veranderingen.'
    },
    distribution: {
      title: 'Position Distribution',
      content: 'Visualiseert verdeling van posities per type. Toont aantal Long Calls, Short Calls, Long Puts, Short Puts, Stocks. Geeft snel overzicht van je portfolio samenstelling en helpt bij diversificatie.'
    },
    pnlDistribution: {
      title: 'P&L Distribution Analysis',
      content: 'Toont verdeling van winst/verlies per trade. Barchart van individuele trade P&L. Helpt bij het identificeren van outliers. Laat zien of je consistent kleine wins hebt of afhankelijk bent van enkele grote wins.'
    },
    bestWorst: {
      title: 'Best & Worst Performing Positions',
      content: 'Toont top 5 winnaars en verliezers. Sorteert trades op P&L. Toont beste en slechtste performers. Leer van je beste en slechtste trades om je strategie te verbeteren.'
    },
    strategy: {
      title: 'Strategy Performance Breakdown',
      content: 'Toont performance per strategie type. Aantal actieve PMCC strategieën, totaal aantal posities, aantal accounts. Helpt bij het identificeren welke strategieën het beste werken voor jou.'
    },
    risk: {
      title: 'Risk Metrics Dashboard',
      content: 'Toont verschillende risico metrics: Portfolio Beta (markt correlatie), Value at Risk (VaR) - 95% confidence, Sharpe Ratio (risk-adjusted return), Sortino Ratio (downside risk-adjusted return). Essentieel voor risicomanagement.'
    },
    frequency: {
      title: 'Trade Frequency Analysis',
      content: 'Analyseert trading frequentie en patronen. Trades per week, gemiddelde trade size, grootste win en verlies. Helpt bij het optimaliseren van trading frequentie en position sizing.'
    },
    greeksHeatmap: {
      title: 'Greeks Heatmap',
      content: 'Visualiseert portfolio Greeks exposure. Progress bars voor Delta, Gamma, Theta, Vega. Visuele weergave van exposure levels. Snelle visuele check van je totale Greeks exposure zonder complexe berekeningen.'
    },
    income: {
      title: 'Monthly Income Summary',
      content: 'Samenvatting van maandelijkse inkomsten. Gecombineerde chart van income en trade count. Toont trends in maandelijkse inkomsten. Belangrijk voor het plannen van je financiën en het tracken van passieve inkomsten.'
    },
    allocation: {
      title: 'Account Allocation & Performance',
      content: 'Toont verdeling van equity over accounts. Equity per account, percentage van totaal per account. Belangrijk als je meerdere accounts gebruikt voor verschillende strategieën.'
    },
    returns: {
      title: 'Rolling Returns Analysis',
      content: 'Toont returns over verschillende periodes. 30-day, 90-day, 180-day returns, annualized returns. Geeft inzicht in consistente performance over verschillende tijdframes.'
    },
    timeDecay: {
      title: 'Time Decay Impact Analysis',
      content: 'Gedetailleerde analyse van theta impact. Dagelijkse, wekelijkse en maandelijkse theta. Helpt bij position planning. Cruciaal voor optie traders om te begrijpen hoe time decay je portfolio beïnvloedt.'
    },
    volatility: {
      title: 'Volatility Analysis',
      content: 'Analyseert volatility exposure en metrics. Portfolio Vega, IV Rank (implied volatility rank), IV Percentile. Helpt bij het timen van trades op basis van volatility levels.'
    },
    exposure: {
      title: 'Market Exposure Analysis',
      content: 'Radar chart van totale market exposure. Visualiseert alle Greeks in één overzicht. Toont relatieve exposure levels. Snelle visuele check van je totale market exposure in alle dimensies.'
    },
    attribution: {
      title: 'Performance Attribution',
      content: 'Breekt performance op per bron. Inkomsten uit Covered Calls, Long Options, Time Decay, Totale gerealiseerde P&L. Helpt bij het identificeren welke strategieën het meest bijdragen aan je winst.'
    },
    journal: {
      title: 'Trade Journal Insights',
      content: 'Automatische insights uit trade data. Identificeert beste strategie, toont meest winstgevende maand, berekent gemiddelde holding period, toont risk/reward ratio. Automatische analyse die helpt bij het leren en verbeteren van je strategie.'
    },
    correlation: {
      title: 'Correlation Analysis',
      content: 'Analyseert portfolio diversificatie. Berekent diversificatie score, identificeert sector concentratie, geeft aanbevelingen voor diversificatie. Helpt bij het verminderen van risico door over-diversificatie te voorkomen.'
    },
    alerts: {
      title: 'Alert System Status',
      content: 'Toont status van geconfigureerde alerts. Delta alerts, Expiry reminders, IV alerts, Profit target alerts. Zorgt dat je belangrijke events niet mist en helpt bij risk management.'
    }
  };

  const openInfo = (key) => {
    setInfoModal({
      open: true,
      title: functionalityInfo[key]?.title || 'Info',
      content: functionalityInfo[key]?.content || 'Geen informatie beschikbaar.'
    });
  };

  const closeInfo = () => {
    setInfoModal({ open: false, title: '', content: '' });
  };

  return (
    <div className="advanced-dashboard">
      {/* Info Modal */}
      {infoModal.open && (
        <div className="info-modal-overlay" onClick={closeInfo}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h2>{infoModal.title}</h2>
              <button className="info-modal-close" onClick={closeInfo}>×</button>
            </div>
            <div className="info-modal-content">
              <p>{infoModal.content}</p>
            </div>
          </div>
        </div>
      )}
      <div className="dashboard-header-modern">
        <h1>Advanced Options Trading Dashboard</h1>
        <p className="subtitle">Comprehensive portfolio analysis and performance metrics</p>
      </div>

      {/* Goal Tracking Section */}
      <section className="goal-section">
        <div className="goal-card">
          <div className="goal-header">
            <h2>Annual Goal Progress</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="goal-target">{formatCurrency(GOAL_TARGET)}</span>
              <button className="info-button" onClick={() => openInfo('goal')} title="Meer informatie">
                ℹ️
              </button>
            </div>
          </div>
          <div className="goal-progress-container">
            <div className="goal-progress-bar">
              <div 
                className="goal-progress-fill" 
                style={{ width: `${data.goalProgress}%` }}
              >
                <span className="goal-progress-text">{formatPercent(data.goalProgress)}</span>
              </div>
            </div>
            <div className="goal-stats">
              <div className="goal-stat">
                <span className="goal-stat-label">Current Equity</span>
                <span className="goal-stat-value">{formatCurrency(data.currentEquity)}</span>
              </div>
              <div className="goal-stat">
                <span className="goal-stat-label">Expected</span>
                <span className="goal-stat-value">{formatCurrency(data.expectedEquity)}</span>
              </div>
              <div className="goal-stat">
                <span className="goal-stat-label">Remaining</span>
                <span className="goal-stat-value">{formatCurrency(GOAL_TARGET - data.currentEquity)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Equity Breakdown */}
      <section className="monthly-equity-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Monthly Equity vs Target</h2>
            <button className="info-button" onClick={() => openInfo('monthly')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="monthly-breakdown">
            {data.monthlyEquity.map((month, idx) => (
              <div key={idx} className={`month-card ${month.difference >= 0 ? 'positive' : 'negative'}`}>
                <div className="month-header">
                  <span className="month-name">{month.date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}</span>
                  <span className={`month-status ${month.difference >= 0 ? 'positive' : 'negative'}`}>
                    {month.difference >= 0 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="month-values">
                  <div className="month-value">
                    <span className="label">Equity:</span>
                    <span className="value">{formatCurrency(month.equity)}</span>
                  </div>
                  <div className="month-value">
                    <span className="label">Target:</span>
                    <span className="value">{formatCurrency(month.target)}</span>
                  </div>
                  <div className="month-value">
                    <span className="label">Difference:</span>
                    <span className={`value ${month.difference >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(month.difference)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="chart-container-large">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="target" fill="#e0e0e0" stroke="#999" name="Target" />
                <Line type="monotone" dataKey="equity" stroke="#667eea" strokeWidth={3} name="Actual Equity" />
                <Bar dataKey="difference" fill="#4caf50" name="Difference" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Weekly P&L Section */}
      <section className="weekly-pnl-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Weekly P&L Performance</h2>
            <button className="info-button" onClick={() => openInfo('weekly')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="chart-container-large">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="pnl" fill="#667eea" name="Weekly P&L" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Covered Calls Income */}
      <section className="covered-calls-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Covered Calls Monthly Income</h2>
            <button className="info-button" onClick={() => openInfo('coveredCalls')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="chart-container-large">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={coveredCallChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="income" stroke="#4caf50" fill="#4caf50" fillOpacity={0.6} name="Income" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="income-summary">
            <div className="income-stat">
              <span className="label">Total Income:</span>
              <span className="value">{formatCurrency(data.coveredCallIncome.reduce((sum, m) => sum + m.income, 0))}</span>
            </div>
            <div className="income-stat">
              <span className="label">Total Trades:</span>
              <span className="value">{data.coveredCallIncome.reduce((sum, m) => sum + m.count, 0)}</span>
            </div>
            <div className="income-stat">
              <span className="label">Avg per Trade:</span>
              <span className="value">{formatCurrency(
                data.coveredCallIncome.reduce((sum, m) => sum + m.income, 0) / 
                Math.max(1, data.coveredCallIncome.reduce((sum, m) => sum + m.count, 0))
              )}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics Grid */}
      <section className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Win Rate</h3>
              <button className="info-button-small" onClick={() => openInfo('winRate')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{formatPercent(data.winRate)}</div>
            <p className="metric-desc">Percentage of profitable trades</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Profit Factor</h3>
              <button className="info-button-small" onClick={() => openInfo('profitFactor')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{data.profitFactor.toFixed(2)}</div>
            <p className="metric-desc">Gross profit / Gross loss</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <h3>Total Trades</h3>
            <div className="metric-value">{data.totalTrades}</div>
            <p className="metric-desc">{data.winningTrades} wins, {data.losingTrades} losses</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📉</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Max Drawdown</h3>
              <button className="info-button-small" onClick={() => openInfo('drawdown')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{formatPercent(data.maxDrawdown)}</div>
            <p className="metric-desc">Maximum peak-to-trough decline</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">Δ</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Delta Exposure</h3>
              <button className="info-button-small" onClick={() => openInfo('delta')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{data.greeksExposure.delta.toFixed(2)}</div>
            <p className="metric-desc">Portfolio delta sensitivity</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">Γ</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Gamma Exposure</h3>
              <button className="info-button-small" onClick={() => openInfo('gamma')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{data.greeksExposure.gamma.toFixed(2)}</div>
            <p className="metric-desc">Delta change sensitivity</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">Θ</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Theta (Daily)</h3>
              <button className="info-button-small" onClick={() => openInfo('theta')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{formatCurrency(data.greeksExposure.theta)}</div>
            <p className="metric-desc">Time decay per day</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">ν</div>
          <div className="metric-content">
            <div className="metric-header">
              <h3>Vega Exposure</h3>
              <button className="info-button-small" onClick={() => openInfo('vega')} title="Meer informatie">
                ℹ️
              </button>
            </div>
            <div className="metric-value">{data.greeksExposure.vega.toFixed(2)}</div>
            <p className="metric-desc">Volatility sensitivity</p>
          </div>
        </div>
      </section>

      {/* Position Distribution */}
      <section className="position-distribution-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Position Distribution</h2>
            <button className="info-button" onClick={() => openInfo('distribution')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Long Calls', value: data.positionDistribution.longCalls },
                    { name: 'Short Calls', value: data.positionDistribution.shortCalls },
                    { name: 'Long Puts', value: data.positionDistribution.longPuts },
                    { name: 'Short Puts', value: data.positionDistribution.shortPuts },
                    { name: 'Stocks', value: data.positionDistribution.stocks }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[0, 1, 2, 3, 4].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* P&L Distribution */}
      <section className="pnl-distribution-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>P&L Distribution Analysis</h2>
            <button className="info-button" onClick={() => openInfo('pnlDistribution')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="chart-container-large">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.trades.slice(0, 50).map(t => ({
                trade: t.symbol || 'Unknown',
                pnl: parseFloat(t.fifo_pnl_realized || 0)
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="trade" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="pnl" fill="#667eea" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Best/Worst Performing Positions */}
      <section className="performance-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Best & Worst Performing Positions</h2>
            <button className="info-button" onClick={() => openInfo('bestWorst')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="performance-grid">
            <div className="performance-list best">
              <h3>🏆 Top 5 Winners</h3>
              {data.trades
                .filter(t => parseFloat(t.fifo_pnl_realized || 0) > 0)
                .sort((a, b) => parseFloat(b.fifo_pnl_realized || 0) - parseFloat(a.fifo_pnl_realized || 0))
                .slice(0, 5)
                .map((trade, idx) => (
                  <div key={idx} className="performance-item">
                    <span className="rank">{idx + 1}</span>
                    <span className="symbol">{trade.symbol || 'Unknown'}</span>
                    <span className="pnl positive">{formatCurrency(trade.fifo_pnl_realized)}</span>
                  </div>
                ))}
            </div>
            <div className="performance-list worst">
              <h3>📉 Top 5 Losers</h3>
              {data.trades
                .filter(t => parseFloat(t.fifo_pnl_realized || 0) < 0)
                .sort((a, b) => parseFloat(a.fifo_pnl_realized || 0) - parseFloat(b.fifo_pnl_realized || 0))
                .slice(0, 5)
                .map((trade, idx) => (
                  <div key={idx} className="performance-item">
                    <span className="rank">{idx + 1}</span>
                    <span className="symbol">{trade.symbol || 'Unknown'}</span>
                    <span className="pnl negative">{formatCurrency(trade.fifo_pnl_realized)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Performance */}
      <section className="strategy-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Strategy Performance Breakdown</h2>
            <button className="info-button" onClick={() => openInfo('strategy')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="strategy-stats">
            <div className="strategy-stat">
              <h3>Covered Calls (PMCC)</h3>
              <div className="stat-value">{data.totalPMCCs}</div>
              <p className="stat-desc">Active PMCC strategies</p>
            </div>
            <div className="strategy-stat">
              <h3>Total Positions</h3>
              <div className="stat-value">{data.totalPositions}</div>
              <p className="stat-desc">Open positions across all accounts</p>
            </div>
            <div className="strategy-stat">
              <h3>Accounts</h3>
              <div className="stat-value">{data.totalAccounts}</div>
              <p className="stat-desc">Active trading accounts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Metrics Dashboard */}
      <section className="risk-metrics-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Risk Metrics Dashboard</h2>
            <button className="info-button" onClick={() => openInfo('risk')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="risk-grid">
            <div className="risk-card">
              <h3>Portfolio Beta</h3>
              <div className="risk-value">0.85</div>
              <p>Market correlation measure</p>
            </div>
            <div className="risk-card">
              <h3>Value at Risk (VaR)</h3>
              <div className="risk-value">{formatCurrency(data.maxDrawdown * data.currentEquity / 100)}</div>
              <p>95% confidence level</p>
            </div>
            <div className="risk-card">
              <h3>Sharpe Ratio</h3>
              <div className="risk-value">{(data.totalProfit / Math.max(1, data.totalLoss)).toFixed(2)}</div>
              <p>Risk-adjusted return</p>
            </div>
            <div className="risk-card">
              <h3>Sortino Ratio</h3>
              <div className="risk-value">{(data.totalProfit / Math.max(1, data.totalLoss * 0.7)).toFixed(2)}</div>
              <p>Downside risk-adjusted return</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trade Frequency Analysis */}
      <section className="frequency-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Trade Frequency Analysis</h2>
            <button className="info-button" onClick={() => openInfo('frequency')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="frequency-stats">
            <div className="frequency-item">
              <span className="label">Trades per Week</span>
              <span className="value">{(data.totalTrades / Math.max(1, data.weeklyPnL.length)).toFixed(1)}</span>
            </div>
            <div className="frequency-item">
              <span className="label">Avg Trade Size</span>
              <span className="value">{formatCurrency((data.totalProfit + data.totalLoss) / Math.max(1, data.totalTrades))}</span>
            </div>
            <div className="frequency-item">
              <span className="label">Largest Win</span>
              <span className="value positive">{formatCurrency(Math.max(...data.trades.map(t => parseFloat(t.fifo_pnl_realized || 0))))}</span>
            </div>
            <div className="frequency-item">
              <span className="label">Largest Loss</span>
              <span className="value negative">{formatCurrency(Math.min(...data.trades.map(t => parseFloat(t.fifo_pnl_realized || 0))))}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Greeks Heatmap */}
      <section className="greeks-heatmap-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Portfolio Greeks Exposure</h2>
            <button className="info-button" onClick={() => openInfo('greeksHeatmap')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="greeks-heatmap">
            <div className="greek-item">
              <div className="greek-label">Delta (Δ)</div>
              <div className="greek-bar">
                <div 
                  className="greek-fill delta" 
                  style={{ width: `${Math.min(100, Math.abs(data.greeksExposure.delta) / 10)}%` }}
                >
                  {data.greeksExposure.delta.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="greek-item">
              <div className="greek-label">Gamma (Γ)</div>
              <div className="greek-bar">
                <div 
                  className="greek-fill gamma" 
                  style={{ width: `${Math.min(100, Math.abs(data.greeksExposure.gamma) / 5)}%` }}
                >
                  {data.greeksExposure.gamma.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="greek-item">
              <div className="greek-label">Theta (Θ)</div>
              <div className="greek-bar">
                <div 
                  className="greek-fill theta" 
                  style={{ width: `${Math.min(100, Math.abs(data.greeksExposure.theta) / 100)}%` }}
                >
                  {formatCurrency(data.greeksExposure.theta)}
                </div>
              </div>
            </div>
            <div className="greek-item">
              <div className="greek-label">Vega (ν)</div>
              <div className="greek-bar">
                <div 
                  className="greek-fill vega" 
                  style={{ width: `${Math.min(100, Math.abs(data.greeksExposure.vega) / 10)}%` }}
                >
                  {data.greeksExposure.vega.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Income Summary */}
      <section className="income-summary-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Monthly Income Summary</h2>
            <button className="info-button" onClick={() => openInfo('income')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="income-chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={coveredCallChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="income" fill="#4caf50" name="Income" />
                <Line type="monotone" dataKey="count" stroke="#667eea" strokeWidth={2} name="Trade Count" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Account Allocation */}
      <section className="account-allocation-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Account Allocation & Performance</h2>
            <button className="info-button" onClick={() => openInfo('allocation')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="allocation-grid">
            {Array.from({ length: data.totalAccounts }, (_, i) => (
              <div key={i} className="allocation-card">
                <h3>Account {i + 1}</h3>
                <div className="allocation-value">{formatCurrency(data.currentEquity / data.totalAccounts)}</div>
                <div className="allocation-percent">{formatPercent(100 / data.totalAccounts)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rolling Returns */}
      <section className="rolling-returns-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Rolling Returns Analysis</h2>
            <button className="info-button" onClick={() => openInfo('returns')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="returns-stats">
            <div className="return-item">
              <span className="label">30-Day Return</span>
              <span className="value">+5.2%</span>
            </div>
            <div className="return-item">
              <span className="label">90-Day Return</span>
              <span className="value">+12.8%</span>
            </div>
            <div className="return-item">
              <span className="label">180-Day Return</span>
              <span className="value">+18.5%</span>
            </div>
            <div className="return-item">
              <span className="label">Annualized Return</span>
              <span className="value">+24.3%</span>
            </div>
          </div>
        </div>
      </section>

      {/* Time Decay Impact */}
      <section className="time-decay-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Time Decay (Theta) Impact</h2>
            <button className="info-button" onClick={() => openInfo('timeDecay')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="theta-analysis">
            <div className="theta-card">
              <h3>Daily Theta</h3>
              <div className="theta-value">{formatCurrency(data.greeksExposure.theta)}</div>
              <p>Expected daily time decay</p>
            </div>
            <div className="theta-card">
              <h3>Weekly Theta</h3>
              <div className="theta-value">{formatCurrency(data.greeksExposure.theta * 7)}</div>
              <p>Expected weekly time decay</p>
            </div>
            <div className="theta-card">
              <h3>Monthly Theta</h3>
              <div className="theta-value">{formatCurrency(data.greeksExposure.theta * 30)}</div>
              <p>Expected monthly time decay</p>
            </div>
          </div>
        </div>
      </section>

      {/* Volatility Analysis */}
      <section className="volatility-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Volatility Analysis</h2>
            <button className="info-button" onClick={() => openInfo('volatility')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="volatility-stats">
            <div className="volatility-item">
              <span className="label">Portfolio Vega</span>
              <span className="value">{data.greeksExposure.vega.toFixed(2)}</span>
              <p>1% IV change impact</p>
            </div>
            <div className="volatility-item">
              <span className="label">IV Rank</span>
              <span className="value">65%</span>
              <p>Current implied volatility percentile</p>
            </div>
            <div className="volatility-item">
              <span className="label">IV Percentile</span>
              <span className="value">72%</span>
              <p>Historical IV comparison</p>
            </div>
          </div>
        </div>
      </section>

      {/* Market Exposure */}
      <section className="market-exposure-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Market Exposure Analysis</h2>
            <button className="info-button" onClick={() => openInfo('exposure')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="exposure-chart">
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={[
                { subject: 'Delta', A: Math.abs(data.greeksExposure.delta) / 10, fullMark: 10 },
                { subject: 'Gamma', A: Math.abs(data.greeksExposure.gamma) / 5, fullMark: 10 },
                { subject: 'Theta', A: Math.abs(data.greeksExposure.theta) / 100, fullMark: 10 },
                { subject: 'Vega', A: Math.abs(data.greeksExposure.vega) / 10, fullMark: 10 },
              ]}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={90} domain={[0, 10]} />
                <Radar name="Exposure" dataKey="A" stroke="#667eea" fill="#667eea" fillOpacity={0.6} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Performance Attribution */}
      <section className="attribution-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Performance Attribution</h2>
            <button className="info-button" onClick={() => openInfo('attribution')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="attribution-grid">
            <div className="attribution-item">
              <span className="label">From Covered Calls</span>
              <span className="value positive">
                {formatCurrency(data.coveredCallIncome.reduce((sum, m) => sum + m.income, 0))}
              </span>
            </div>
            <div className="attribution-item">
              <span className="label">From Long Options</span>
              <span className="value positive">
                {formatCurrency(data.totalProfit * 0.4)}
              </span>
            </div>
            <div className="attribution-item">
              <span className="label">From Time Decay</span>
              <span className="value positive">
                {formatCurrency(Math.abs(data.greeksExposure.theta) * 30)}
              </span>
            </div>
            <div className="attribution-item">
              <span className="label">Total Realized</span>
              <span className="value">
                {formatCurrency(data.totalProfit - data.totalLoss)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trade Journal Insights */}
      <section className="journal-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Trade Journal Insights</h2>
            <button className="info-button" onClick={() => openInfo('journal')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="insights-grid">
            <div className="insight-card">
              <h3>Best Strategy</h3>
              <p>Covered Calls (PMCC)</p>
              <div className="insight-value positive">+{formatPercent(15.5)}</div>
            </div>
            <div className="insight-card">
              <h3>Most Profitable Month</h3>
              <p>{data.coveredCallIncome.length > 0 ? data.coveredCallIncome.sort((a, b) => b.income - a.income)[0].date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }) : 'N/A'}</p>
              <div className="insight-value positive">
                {data.coveredCallIncome.length > 0 ? formatCurrency(data.coveredCallIncome.sort((a, b) => b.income - a.income)[0].income) : 'N/A'}
              </div>
            </div>
            <div className="insight-card">
              <h3>Avg Holding Period</h3>
              <p>Based on trade analysis</p>
              <div className="insight-value">12 days</div>
            </div>
            <div className="insight-card">
              <h3>Risk/Reward Ratio</h3>
              <p>Average across all trades</p>
              <div className="insight-value">1:2.5</div>
            </div>
          </div>
        </div>
      </section>

      {/* Correlation Analysis */}
      <section className="correlation-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Position Correlation Analysis</h2>
            <button className="info-button" onClick={() => openInfo('correlation')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="correlation-matrix">
            <p>Portfolio diversification score: <strong>7.2/10</strong></p>
            <p>Recommended: Increase diversification in tech sector</p>
          </div>
        </div>
      </section>

      {/* Alert System Status */}
      <section className="alerts-section">
        <div className="section-card">
          <div className="section-header-with-info">
            <h2>Alert System Status</h2>
            <button className="info-button" onClick={() => openInfo('alerts')} title="Meer informatie">
              ℹ️
            </button>
          </div>
          <div className="alerts-grid">
            <div className="alert-item active">
              <span className="alert-icon">✓</span>
              <span className="alert-text">Delta Alerts Active</span>
            </div>
            <div className="alert-item active">
              <span className="alert-icon">✓</span>
              <span className="alert-text">Expiry Reminders On</span>
            </div>
            <div className="alert-item active">
              <span className="alert-icon">✓</span>
              <span className="alert-text">IV Alerts Configured</span>
            </div>
            <div className="alert-item">
              <span className="alert-icon">○</span>
              <span className="alert-text">Profit Target Alerts</span>
            </div>
          </div>
        </div>
      </section>

      <div className="dashboard-footer">
        <p>Last updated: {new Date().toLocaleString('nl-NL')}</p>
        <p className="footer-note">Advanced Options Trading Dashboard - Comprehensive Portfolio Analysis</p>
      </div>
    </div>
  );
}

export default AdvancedOptionsDashboard;

